<script>
  import { tick } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import SideSheet from '../components/SideSheet.svelte';
  import { customersApi } from '../stores/api.js';
  import { activeCompanyId, activeCompany } from '../stores/company.js';
  import { canWriteInCompany } from '../stores/capabilities.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { isEmptyOrValidEmail } from '../lib/email-address.js';
  import {
    SEDE_FIELDS, CAP_ESTERO, NAZIONE_DEFAULT,
    isSedeEmpty, isSedeIncomplete, formatSede, sedePayload, sedeProblems,
  } from '../lib/customer-sede.js';
  import { Users, Plus, Edit, Trash2, Search, AlertTriangle } from '../lib/icons/index.js';

  let customers = [];
  let loading = true;
  let error = '';
  let searchQuery = '';
  // The create and edit form lives in a side sheet. `editingId` is the
  // anagrafica being edited, or null when the sheet creates a new one: one
  // form serves both, as in PaymentMethods.
  let sheetOpen = false;
  let editingId = null;
  let editingName = '';
  let saving = false;
  let sheetError = null;
  // A field's format problem shows while it is typed; a field left empty only
  // once the user has tried to save — an empty form is not an error.
  let submitAttempted = false;
  // The address as it was loaded. An anagrafica imported with half an address
  // stays editable for its email or its IBAN: the address is judged, and sent,
  // only when it was changed.
  let loadedSedeKey = '';
  // kind tab: 'cliente' | 'fornitore' — backend treats 'entrambi' rows as
  // matching both tabs, so a dual-role anagrafica appears under each.
  let kindTab = 'cliente';

  // Quante anagrafiche soggette — o potenzialmente soggette — alla scissione
  // dei pagamenti non hanno ancora un'esigibilità IVA registrata. Non sono solo
  // le PA: un ente che riceve come FPR12 su un codice destinatario ordinario non
  // viene bloccato da nulla, e la sua fattura ricade in silenzio sull'immediata.
  // Il server le riconosce dallo storico dell'azienda (una fattura già emessa
  // con esigibilità S), quindi l'elenco arriva da lì e non si indovina qui.
  let paSenzaEsigibilita = 0;
  // Gli id di quelle anagrafiche, per marcare le righe dell'elenco completo.
  let senzaEsigibilitaIds = new Set();
  // Quando è attivo, l'elenco mostra soltanto quelle anagrafiche.
  let soloSenzaEsigibilita = false;

  /** The payment methods the select offers; one stored outside them is kept and shown as it is. */
  const MODALITA_PAGAMENTO = ['MP05', 'MP01', 'MP08', 'MP12'];

  function emptyForm(kind) {
    return {
      tipo: 'azienda', kind, ragioneSociale: '', nome: '', cognome: '',
      partitaIva: '', codiceFiscale: '', codiceDestinatario: '0000000',
      pecDestinatario: '', idPaese: 'IT', email: '', telefono: '', fax: '',
      sede: { indirizzo: '', numeroCivico: '', cap: '', comune: '', provincia: '', nazione: NAZIONE_DEFAULT },
      condPagamento: 'TP02', modalitaPagamento: 'MP05', iban: '', scadenzaGiorni: 30,
      esigibilitaIva: '', note: '',
    };
  }

  let form = emptyForm('cliente');
  $: emailInvalid = !isEmptyOrValidEmail(form.email);

  $: companyId = $activeCompanyId;

  // One trigger, not three. This used to be an onMount plus two reactive
  // statements that both depended on companyId, so the first render fired
  // three identical requests; the single statement below already covers the
  // mount, a company switch and a tab switch.
  $: if (companyId && kindTab) loadCustomers();

  // `quiet` keeps the table on screen while it refreshes: after a save from the
  // sheet, focus goes back to the row's own Modifica button, which a table
  // swapped for the loading line would have destroyed.
  async function loadCustomers({ quiet = false } = {}) {
    if (!companyId) return;
    if (!quiet) loading = true;
    // Cleared on entry: without this a single failed load left the red banner
    // on screen forever, through every later success and every tab switch.
    error = '';
    try {
      customers = await customersApi.list(companyId, {
        kind: kindTab,
        ...(soloSenzaEsigibilita ? { destinazione: 'scissione', esigibilitaIva: 'mancante' } : {}),
      });
      await contaSenzaEsigibilita();
    } catch (err) {
      error = err.message;
    }
    loading = false;
  }

  /**
   * Conta le anagrafiche PA senza esigibilità IVA.
   *
   * Con il filtro attivo l'elenco appena caricato *è* già quell'insieme, quindi
   * non si richiede due volte la stessa cosa al server. Un errore qui non
   * diventa mai il banner rosso: è un avviso, e un avviso che non si riesce a
   * calcolare non deve coprire l'elenco che invece è arrivato.
   */
  async function contaSenzaEsigibilita() {
    if (kindTab !== 'cliente') {
      paSenzaEsigibilita = 0;
      return;
    }
    if (soloSenzaEsigibilita) {
      paSenzaEsigibilita = customers.length;
      senzaEsigibilitaIds = new Set(customers.map((c) => c.id));
      return;
    }
    try {
      const mancanti = await customersApi.list(companyId, {
        kind: 'cliente', destinazione: 'scissione', esigibilitaIva: 'mancante',
      });
      paSenzaEsigibilita = mancanti.length;
      // Serve anche riga per riga: chi manca all'appello per via dello storico
      // non ha nulla addosso che si possa riconoscere dal browser.
      senzaEsigibilitaIds = new Set(mancanti.map((c) => c.id));
    } catch {
      paSenzaEsigibilita = 0;
      senzaEsigibilitaIds = new Set();
    }
  }

  // ── Registrazione in blocco dell'esigibilità IVA ─────────────────────
  //
  // La stessa cosa che la riga di comando fa con `esigibilita-pa --all --set`,
  // e che dal browser costringeva ad aprire una scheda per cliente. Resta una
  // scelta deliberata: nessun codice è preselezionato, l'elenco esatto delle
  // anagrafiche che verranno toccate è sotto gli occhi mentre si conferma, e
  // ogni riga si può togliere. Non c'è, e non ci sarà, un pulsante che sceglie
  // al posto dell'utente: indicare l'esigibilità sbagliata significa chiedere
  // a un ente un'IVA che non può pagare.
  //
  // Le scritture sono singole PUT, una per anagrafica: è anche il motivo per
  // cui una modifica successiva sulla singola scheda vince sempre: sovrascrive
  // lo stesso campo, sullo stesso record, senza niente in mezzo che la ripeta.
  let bulkOpen = false;
  let bulkCode = '';
  let bulkTargets = [];
  let bulkSelectedIds = [];
  let bulkLoadingTargets = false;
  let bulkApplying = false;
  let bulkError = '';
  let bulkResult = '';

  async function openBulk() {
    bulkOpen = true;
    bulkError = '';
    bulkResult = '';
    // Proposta, non scorciatoia: verso la Pubblica Amministrazione la
    // scissione dei pagamenti è il caso ordinario, e partire dal codice giusto
    // evita che l'operatore scelga a caso pur di sbloccare le fatture. Resta
    // comunque un gesto esplicito — le anagrafiche toccate sono elencate qui
    // sotto e l'applicazione va confermata.
    bulkCode = 'S';
    await loadBulkTargets();
  }

  function closeBulk() {
    bulkOpen = false;
    bulkError = '';
    bulkResult = '';
  }

  /**
   * L'elenco esatto su cui la registrazione agirà, richiesto al server nel
   * momento in cui si apre il pannello — non filtrato dalla ricerca a schermo,
   * che nasconderebbe righe che verrebbero comunque modificate.
   */
  async function loadBulkTargets() {
    bulkLoadingTargets = true;
    try {
      bulkTargets = await customersApi.list(companyId, {
        kind: 'cliente', destinazione: 'scissione', esigibilitaIva: 'mancante',
      });
      bulkSelectedIds = bulkTargets.map((c) => c.id);
    } catch (err) {
      bulkTargets = [];
      bulkSelectedIds = [];
      bulkError = err.message;
    }
    bulkLoadingTargets = false;
  }

  function toggleBulkTarget(id) {
    bulkSelectedIds = bulkSelectedIds.includes(id)
      ? bulkSelectedIds.filter((x) => x !== id)
      : [...bulkSelectedIds, id];
  }

  $: bulkSelectedCount = bulkSelectedIds.length;

  async function applyBulk() {
    if (!bulkCode || bulkSelectedCount === 0) return;
    bulkApplying = true;
    bulkError = '';
    bulkResult = '';
    let done = 0;
    let failed = 0;
    for (const id of bulkSelectedIds) {
      try {
        // Solo il campo che stiamo registrando: rimandare tutta l'anagrafica
        // significherebbe riscrivere dati che nessuno ha chiesto di cambiare.
        await customersApi.update(companyId, id, { esigibilitaIva: bulkCode });
        done += 1;
      } catch {
        failed += 1;
      }
    }
    bulkResult = failed === 0
      ? tImmediate('customers.esigibilita.bulk.done', { codice: bulkCode, conteggio: done })
      : tImmediate('customers.esigibilita.bulk.partial', { codice: bulkCode, conteggio: done, falliti: failed });
    bulkApplying = false;
    await loadCustomers();
    await loadBulkTargets();
  }

  async function toggleSoloSenzaEsigibilita() {
    soloSenzaEsigibilita = !soloSenzaEsigibilita;
    searchQuery = '';
    await loadCustomers();
  }

  // Roving-tabindex traversal for the clienti/fornitori tablist.
  const KIND_TABS = ['cliente', 'fornitore'];
  function onKindKeydown(e) {
    const i = KIND_TABS.indexOf(kindTab);
    let next = null;
    if (e.key === 'ArrowRight') next = KIND_TABS[(i + 1) % KIND_TABS.length];
    else if (e.key === 'ArrowLeft') next = KIND_TABS[(i - 1 + KIND_TABS.length) % KIND_TABS.length];
    else if (e.key === 'Home') next = KIND_TABS[0];
    else if (e.key === 'End') next = KIND_TABS[KIND_TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    kindTab = next;
    soloSenzaEsigibilita = false;
    tick().then(() => document.getElementById(`customers-tab-${next}`)?.focus());
  }

  // Il Codice Destinatario è di 7 caratteri per imprese e privati e di 6 (il
  // Codice Univoco Ufficio) per la Pubblica Amministrazione. La lunghezza
  // sbagliata fa scartare la fattura da SdI, quindi va detta prima dell'invio.
  $: codiceDestinatarioLength = form.tipo === 'pa' ? 6 : 7;
  $: codiceDestinatarioMismatch = !!form.codiceDestinatario
    && form.codiceDestinatario.length > 0
    && form.codiceDestinatario.length !== codiceDestinatarioLength;

  // La sede, con le stesse regole che il server applica (lib/customer-sede.js).
  $: sedeChanged = !editingId || JSON.stringify(sedePayload(form.sede)) !== loadedSedeKey;
  $: sedeVuota = isSedeEmpty(form.sede);
  $: sedeErrors = sedeChanged ? sedeProblems(form.sede) : {};
  $: shownSedeErrors = Object.fromEntries(SEDE_FIELDS.map((field) => [
    field,
    sedeErrors[field] && (submitAttempted || sedeErrors[field] !== 'required') ? sedeErrors[field] : '',
  ]));
  // Caricata incompleta e non toccata: si salva lo stesso, ma lo si dice.
  $: sedeIncompletaInvariata = !sedeChanged && !sedeVuota && isSedeIncomplete(form.sede);
  $: sedeEstera = !!form.sede.nazione.trim() && form.sede.nazione.trim().toUpperCase() !== NAZIONE_DEFAULT;

  /** Ids of a sede field's hint and, when there is one, of its error. */
  const describedBy = (field, hintId, shown) => [hintId, shown[field] ? `cust-sede-${field}-error` : '']
    .filter(Boolean).join(' ');

  // Fuori dall'Italia la fattura vuole 00000: proposto, mai imposto sopra un
  // CAP già scritto; e tolto se si torna all'Italia.
  function onNazioneInput() {
    const nazione = form.sede.nazione.trim().toUpperCase();
    if (nazione && nazione !== NAZIONE_DEFAULT && !form.sede.cap.trim()) form.sede.cap = CAP_ESTERO;
    else if (nazione === NAZIONE_DEFAULT && form.sede.cap === CAP_ESTERO) form.sede.cap = '';
  }

  function openCreate() {
    editingId = null;
    editingName = '';
    // The new row starts in the tab that is open, so it appears there at once.
    form = emptyForm(kindTab);
    loadedSedeKey = '';
    submitAttempted = false;
    sheetError = null;
    sheetOpen = true;
  }

  /**
   * Load an existing anagrafica into the sheet. The `kind` travels with it so
   * saving an edit cannot silently move a fornitore into the clienti tab just
   * because that tab happened to be open. A payment default the anagrafica does
   * not have stays empty: proposing one here would write it on save.
   */
  function openEdit(customer) {
    const text = (value) => (value === null || value === undefined ? '' : String(value));
    editingId = customer.id;
    editingName = displayName(customer);
    form = {
      tipo: customer.tipo || 'azienda',
      kind: customer.kind || kindTab,
      ragioneSociale: text(customer.ragioneSociale),
      nome: text(customer.nome),
      cognome: text(customer.cognome),
      partitaIva: text(customer.partitaIva),
      codiceFiscale: text(customer.codiceFiscale),
      codiceDestinatario: customer.codiceDestinatario || '0000000',
      pecDestinatario: text(customer.pecDestinatario),
      idPaese: customer.idPaese || 'IT',
      email: text(customer.contatti?.email),
      telefono: text(customer.contatti?.telefono),
      fax: text(customer.contatti?.fax),
      sede: {
        indirizzo: text(customer.sede?.indirizzo),
        numeroCivico: text(customer.sede?.numeroCivico),
        cap: text(customer.sede?.cap),
        comune: text(customer.sede?.comune),
        provincia: text(customer.sede?.provincia),
        nazione: text(customer.sede?.nazione) || NAZIONE_DEFAULT,
      },
      condPagamento: customer.condPagamento || '',
      modalitaPagamento: customer.modalitaPagamento || '',
      iban: text(customer.iban),
      scadenzaGiorni: customer.scadenzaGiorni ?? '',
      esigibilitaIva: customer.esigibilitaIva || '',
      note: text(customer.note),
    };
    loadedSedeKey = JSON.stringify(sedePayload(form.sede));
    submitAttempted = false;
    sheetError = null;
    sheetOpen = true;
  }

  function closeSheet() {
    if (saving) return;
    sheetOpen = false;
    sheetError = null;
    submitAttempted = false;
  }

  /**
   * The body the API takes. `contatti` and `sede` are merged into what is
   * stored, so an empty field is sent as '' — which removes it — and the
   * address goes only when it changed (null when it was emptied).
   */
  function customerPayload() {
    const { email, telefono, fax, sede, ...fields } = form;
    const payload = {
      ...fields,
      idPaese: fields.idPaese.trim().toUpperCase() || 'IT',
      condPagamento: fields.condPagamento || null,
      modalitaPagamento: fields.modalitaPagamento || null,
      iban: fields.iban.trim() || null,
      scadenzaGiorni: fields.scadenzaGiorni === '' || fields.scadenzaGiorni === null
        ? null : Number(fields.scadenzaGiorni),
      note: fields.note.trim() || null,
      contatti: { email: email.trim(), telefono: telefono.trim(), fax: fax.trim() },
    };
    if (sedeChanged) payload.sede = isSedeEmpty(sede) ? null : sedePayload(sede);
    return payload;
  }

  async function focusField(id) {
    await tick();
    document.getElementById(id)?.focus();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    submitAttempted = true;
    sheetError = null;
    if (emailInvalid) {
      focusField('cust-email');
      return;
    }
    const problems = sedeChanged ? sedeProblems(form.sede) : {};
    const firstProblem = SEDE_FIELDS.find((field) => problems[field]);
    if (firstProblem) {
      focusField(`cust-sede-${firstProblem}`);
      return;
    }
    saving = true;
    try {
      if (editingId) {
        await customersApi.update(companyId, editingId, customerPayload());
      } else {
        await customersApi.create(companyId, customerPayload());
      }
      await loadCustomers({ quiet: true });
      saving = false;
      closeSheet();
    } catch (err) {
      saving = false;
      sheetError = err;
      // The server names the field an address or contacts refusal is about.
      if (err?.code?.startsWith('sede_') && err.field) focusField(`cust-sede-${err.field}`);
      else if (err?.code?.startsWith('contatti_') && err.field) focusField(`cust-${err.field}`);
    }
  }

  async function handleDelete(id) {
    if (!confirm(tImmediate('customers.confirmDelete'))) return;
    try {
      await customersApi.remove(companyId, id);
      await loadCustomers();
    } catch (err) {
      error = err.message;
    }
  }
  function displayName(c) {
    if (c.tipo === 'persona_fisica') return `${c.nome} ${c.cognome}`;
    return c.ragioneSociale || '-';
  }

  // Il Codice Destinatario di un ente pubblico è il Codice Univoco Ufficio, di
  // 6 caratteri; quello di imprese e privati ne ha 7. È l'unico dato
  // dell'anagrafica che dice «a questo cliente si emette una FPA12», ed è lì
  // che l'esigibilità IVA non può essere lasciata al caso.
  const isPa = (c) => c.tipo === 'pa' || String(c.codiceDestinatario || '').trim().length === 6;

  $: filteredCustomers = searchQuery
    ? customers.filter(c => {
        const q = searchQuery.toLowerCase();
        return displayName(c).toLowerCase().includes(q)
          || (c.partitaIva || '').includes(q)
          || (c.codiceFiscale || '').toLowerCase().includes(q)
          || String(c.sede?.comune || '').toLowerCase().includes(q);
      })
    : customers;
</script>

<Layout>
  <div>
    <div class="view-head">
      <div class="view-head-copy">
        <h1 class="section-title">{$t('customers.pageTitle')}</h1>
        <p class="view-subtitle">{$t('customers.pageSubtitle')}</p>
      </div>
      {#if $canWriteInCompany}
        <div class="view-actions">
          <button type="button" on:click={openCreate}
            class="btn btn-primary gap-2">
            <Plus size={16} aria-hidden="true" /><span>{$t(`customers.tabs.${kindTab}New`)}</span>
          </button>
        </div>
      {/if}
    </div>

    {#if !companyId}
      <div class="card p-8 text-center" style="color: var(--color-text-muted);">
        {$t('customers.selectCompanyFirst')}
      </div>
    {:else}
      <ErrorBanner {error} />

      <!-- Kind tabs: clienti / fornitori. "entrambi" rows appear in both.
           Announced as a tab set: the active tab was previously distinguishable
           only by its colour, which conveys nothing to a screen reader. -->
      <div class="tab-bar mb-4"><div class="tab-list" role="tablist" aria-label={$t('customers.tabs.groupLabel')}>
        <button type="button" role="tab" id="customers-tab-cliente"
          aria-selected={kindTab === 'cliente'} aria-controls="customers-panel"
          tabindex={kindTab === 'cliente' ? 0 : -1}
          on:click={() => { kindTab = 'cliente'; soloSenzaEsigibilita = false; }} on:keydown={onKindKeydown}
          class="tab-button">
          {$t('customers.tabs.cliente')}
          {#if kindTab === 'cliente'}<span class="tab-count"><span class="sr-only">, </span>{filteredCustomers.length}</span>{/if}
        </button>
        <button type="button" role="tab" id="customers-tab-fornitore"
          aria-selected={kindTab === 'fornitore'} aria-controls="customers-panel"
          tabindex={kindTab === 'fornitore' ? 0 : -1}
          on:click={() => { kindTab = 'fornitore'; soloSenzaEsigibilita = false; }} on:keydown={onKindKeydown}
          class="tab-button">
          {$t('customers.tabs.fornitore')}
          {#if kindTab === 'fornitore'}<span class="tab-count"><span class="sr-only">, </span>{filteredCustomers.length}</span>{/if}
        </button>
      </div></div>

      <div id="customers-panel" role="tabpanel" tabindex="0" aria-labelledby="customers-tab-{kindTab}">

      <!-- L'avviso che deve arrivare prima della prima fattura rifiutata.
           L'esigibilità IVA non si può indovinare al posto dell'utente, quindi
           l'unica cosa onesta da fare è dire chiaramente quali anagrafiche la
           stanno aspettando e portarcelo sopra in un clic. -->
      {#if kindTab === 'cliente' && paSenzaEsigibilita > 0}
        <div class="alert alert-warning mb-4" role="status">
          <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
          <div class="space-y-2">
            <p class="alert-title">
              {$t('customers.esigibilita.alertTitle', { conteggio: paSenzaEsigibilita })}
            </p>
            <p class="alert-message measure">
              {$t('customers.esigibilita.alertBody')}
            </p>
            <p class="alert-message measure">
              {$t('customers.esigibilita.alertHowTo')}
            </p>
            <div class="flex flex-wrap gap-2">
              <button type="button" on:click={toggleSoloSenzaEsigibilita}
                class="btn btn-secondary btn-sm">
                {soloSenzaEsigibilita
                  ? $t('customers.esigibilita.alertActionClear')
                  : $t('customers.esigibilita.alertAction')}
              </button>
              {#if $canWriteInCompany}
                <button type="button" on:click={() => (bulkOpen ? closeBulk() : openBulk())}
                  class="btn btn-secondary btn-sm">
                  {bulkOpen
                    ? $t('customers.esigibilita.bulk.close')
                    : $t('customers.esigibilita.bulk.open')}
                </button>
              {/if}
            </div>
          </div>
        </div>
      {/if}

      <!-- Registrazione in blocco. Sta sotto l'avviso che l'ha motivata, e
           mostra per intero l'elenco che modificherà: applicare un codice a
           quaranta clienti senza vedere quali sono non è una scelta, è un
           azzardo. -->
      {#if bulkOpen && kindTab === 'cliente' && $canWriteInCompany}
        <div class="card p-6 mb-4">
          <h2 class="text-lg font-bold mb-1" style="color: var(--color-text-heading);">
            {$t('customers.esigibilita.bulk.title')}
          </h2>
          <p class="text-sm measure mb-4" style="color: var(--color-text-muted);">
            {$t('customers.esigibilita.bulk.intro')}
          </p>

          <ErrorBanner error={bulkError} />

          {#if bulkResult}
            <div role="status" class="mb-4 p-3 border-w text-sm"
              style="border-color: var(--color-tint-medium); color: var(--color-text-body);">
              {bulkResult}
            </div>
          {/if}

          <div class="mb-4">
            <label for="bulk-esigibilita" class="block eyebrow mb-1" style="color: var(--color-text-muted);">
              {$t('customers.esigibilita.bulk.pickLabel')}
            </label>
            <select id="bulk-esigibilita" bind:value={bulkCode}
              class="input">
              <option value="">{$t('customers.esigibilita.bulk.pickNone')}</option>
              <option value="I">{$t('customers.esigibilita.I')}</option>
              <option value="D">{$t('customers.esigibilita.D')}</option>
              <option value="S">{$t('customers.esigibilita.bulkS')}</option>
            </select>
            <!-- Cosa significa il codice scelto, prima di applicarlo a decine
                 di anagrafiche in una volta. -->
            <p class="mt-2 text-xs measure" style="color: var(--color-text-muted);">
              {bulkCode
                ? $t(`invoiceCreate.esigibilita.explain.${bulkCode}`)
                : $t('customers.esigibilita.bulk.pickHelp')}
            </p>
          </div>

          <div class="mb-4">
            <p class="eyebrow mb-1" style="color: var(--color-text-muted);">
              {$t('customers.esigibilita.bulk.targetsTitle', { n: bulkTargets.length })}
            </p>
            <p class="text-xs measure mb-2" style="color: var(--color-text-muted);">
              {$t('customers.esigibilita.bulk.targetsHint')}
            </p>

            {#if bulkLoadingTargets}
              <p class="text-sm" style="color: var(--color-text-muted);">{$t('app.loading')}</p>
            {:else if bulkTargets.length === 0}
              <p class="text-sm" style="color: var(--color-text-muted);">{$t('customers.esigibilita.bulk.empty')}</p>
            {:else}
              <div class="flex flex-wrap gap-2 mb-2">
                <button type="button" on:click={() => bulkSelectedIds = bulkTargets.map(c => c.id)}
                  class="btn btn-secondary btn-sm">
                  {$t('customers.esigibilita.bulk.selectAll')}
                </button>
                <button type="button" on:click={() => bulkSelectedIds = []}
                  class="btn btn-secondary btn-sm">
                  {$t('customers.esigibilita.bulk.clearAll')}
                </button>
                <span class="self-center text-xs font-mono" style="color: var(--color-text-muted);">
                  {$t('customers.esigibilita.bulk.selectedCount', { n: bulkSelectedCount })}
                </span>
              </div>
              <ul class="border-w max-h-64 overflow-y-auto" style="border-color: var(--color-tint-soft);">
                {#each bulkTargets as target}
                  <li class="border-b last:border-b-0" style="border-color: var(--color-tint-soft);">
                    <label class="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={bulkSelectedIds.includes(target.id)}
                        on:change={() => toggleBulkTarget(target.id)} />
                      <span class="font-mono" style="color: var(--color-text-body);">{displayName(target)}</span>
                      <span class="font-mono text-xs ml-auto" style="color: var(--color-text-muted);">
                        {target.codiceDestinatario || '-'}
                      </span>
                    </label>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>

          <p class="text-xs measure mb-4" style="color: var(--color-text-muted);">
            {$t('customers.esigibilita.bulk.reversible')}
          </p>

          <div class="flex flex-wrap items-center gap-3">
            <button type="button" on:click={applyBulk} disabled={!bulkCode || bulkSelectedCount === 0 || bulkApplying}
              class="btn btn-primary btn-sm">
              {bulkApplying
                ? $t('customers.esigibilita.bulk.applying')
                : $t('customers.esigibilita.bulk.confirm', { n: bulkSelectedCount })}
            </button>
            <button type="button" on:click={closeBulk}
              class="btn btn-secondary btn-sm">
              {$t('common.cancel')}
            </button>
            {#if !bulkCode}
              <span class="text-xs" style="color: var(--color-text-muted);">{$t('customers.esigibilita.bulk.needCode')}</span>
            {:else if bulkSelectedCount === 0}
              <span class="text-xs" style="color: var(--color-text-muted);">{$t('customers.esigibilita.bulk.needTargets')}</span>
            {/if}
          </div>
        </div>
      {/if}

      {#if soloSenzaEsigibilita}
        <p class="mb-3 text-xs font-mono" style="color: var(--color-text-muted);">
          {$t('customers.esigibilita.filterActive')}
        </p>
      {/if}

      <!-- Search bar -->
      <div class="search-field mb-4">
        <Search size={16} class="search-icon" aria-hidden="true" />
        <input bind:value={searchQuery} placeholder={$t('customers.searchPlaceholder')}
          aria-label={$t('customers.searchPlaceholder')}
          class="input" />
      </div>

      {#if loading}
        <div class="text-center py-8" style="color: var(--color-text-muted);">{$t('app.loading')}</div>
      {:else if filteredCustomers.length === 0}
        <div class="card p-8 text-center">
          <Users size={48} class="mx-auto mb-4 opacity-30" style="color: var(--color-text-muted);" />
          <p style="color: var(--color-text-muted);">
            {#if soloSenzaEsigibilita && !searchQuery}
              {$t('customers.esigibilita.filterEmpty')}
            {:else}
              {searchQuery ? $t('customers.noneFound') : $t(`customers.tabs.${kindTab}Empty`)}
            {/if}
          </p>
        </div>
      {:else}
        <div class="overflow-x-auto">
          <table class="table-modern table-stack w-full" role="table">
            <thead class="table-header" role="rowgroup">
              <tr role="row">
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('customers.fields.nameOrCompanyName')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('customers.sede.column')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('customers.fields.partitaIvaShort')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('customers.fields.codiceDestinatarioShort')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('customers.fields.esigibilitaIvaShort')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('customers.fields.type')}</th>
                <th class="table-cell" role="columnheader"><span class="sr-only">{$t('customers.actions')}</span></th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {#each filteredCustomers as customer (customer.id)}
                <tr class="table-row" role="row" style="cursor: default;">
                  <td role="cell" data-label={$t('customers.fields.nameOrCompanyName')} class="table-cell whitespace-normal cell-strong">{displayName(customer)}</td>
                  <!-- The address a document to this counterpart will carry,
                       and a flag when it cannot carry one yet. -->
                  <td role="cell" data-label={$t('customers.sede.column')} class="table-cell whitespace-normal text-xs">
                    {#if formatSede(customer.sede)}
                      <span class="block" style="color: var(--color-text-body);">{formatSede(customer.sede)}</span>
                    {/if}
                    {#if isSedeIncomplete(customer.sede)}
                      <span class="badge badge-yellow mt-1" title={$t('customers.sede.missingTooltip')}>
                        {isSedeEmpty(customer.sede) ? $t('customers.sede.missing') : $t('customers.sede.incomplete')}
                      </span>
                    {/if}
                  </td>
                  <td role="cell" data-label={$t('customers.fields.partitaIvaShort')} class="table-cell mono text-xs text-muted">{customer.partitaIva || '-'}</td>
                  <td role="cell" data-label={$t('customers.fields.codiceDestinatarioShort')} class="table-cell mono text-xs text-muted">{customer.codiceDestinatario}</td>
                  <td role="cell" data-label={$t('customers.fields.esigibilitaIvaShort')} class="table-cell text-xs">
                    {#if customer.esigibilitaIva}
                      <span class="font-mono" style="color: var(--color-text-body);"
                        title={$t(`customers.esigibilita.${customer.esigibilitaIva}`, undefined, customer.esigibilitaIva)}>
                        {customer.esigibilitaIva}
                      </span>
                    {:else if senzaEsigibilitaIds.has(customer.id) || isPa(customer)}
                      <span class="badge badge-yellow"
                        title={$t('customers.esigibilita.missingTooltip')}>
                        {$t('customers.esigibilita.missing')}
                      </span>
                    {:else}
                      <span style="color: var(--color-text-muted);">-</span>
                    {/if}
                  </td>
                  <td role="cell" data-label={$t('customers.fields.type')} class="table-cell">
                    <div class="flex items-center gap-2">
                      <span class="badge badge-gray">{$t(`customers.types.${customer.tipo}`, undefined, customer.tipo)}</span>
                      {#if customer.kind === 'entrambi'}
                        <span class="badge badge-blue" title={$t('customers.kindBadges.entrambiTooltip')}>{$t('customers.kindBadges.entrambi')}</span>
                      {/if}
                    </div>
                  </td>
                  <td role="cell" data-label="" class="table-cell text-right">
                    {#if $canWriteInCompany}
                      <div class="flex items-center gap-1 justify-end">
                        <button on:click={() => openEdit(customer)} class="icon-button"
                          title={$t('customers.editAction')} aria-label={$t('customers.editActionLabel', { name: displayName(customer) })}>
                          <Edit size={16} aria-hidden="true" />
                        </button>
                        <button on:click={() => handleDelete(customer.id)} class="icon-button hover:text-danger"
                          title={$t('common.delete')} aria-label={$t('customers.deleteActionLabel', { name: displayName(customer) })}>
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
      </div>
    {/if}
  </div>
  <!-- The create and edit form: a sheet from the right edge, over the list. -->
  {#if $canWriteInCompany}
    <SideSheet
      open={sheetOpen}
      id="customer-sheet"
      eyebrow={form.kind === 'fornitore' ? $t('customers.sheet.eyebrowFornitore') : $t('customers.sheet.eyebrowCliente')}
      title={editingId ? (editingName || $t('customers.editTitle')) : $t(`customers.tabs.${kindTab}New`)}
      closeLabel={$t('customers.sheet.close')}
      on:close={closeSheet}
    >
      <form id="customer-sheet-form" on:submit={handleSubmit}>
        <p class="sheet-section-intro">{editingId ? $t('customers.editHint') : $t('customers.newHint')}</p>

        <ErrorBanner error={sheetError} />

        <section class="sheet-section" aria-labelledby="cust-section-identity">
          <h3 id="cust-section-identity" class="sheet-section-title">{$t('customers.sections.identity.title')}</h3>
          <p class="sheet-section-intro">{$t('customers.sections.identity.intro')}</p>
          <div class="sheet-fields">
            <div>
            <div class="sheet-row">
              <div>
                <label for="cust-tipo" class="label">{$t('customers.fields.type')}</label>
                <select id="cust-tipo" bind:value={form.tipo} class="input">
                  <option value="azienda">{$t('customers.types.azienda')}</option>
                  <option value="persona_fisica">{$t('customers.types.persona_fisica')}</option>
                  <option value="pa">{$t('customers.types.pa')}</option>
                </select>
              </div>
              <div>
                <label for="cust-kind" class="label">{$t('customers.fields.kind')}</label>
                <select id="cust-kind" bind:value={form.kind} class="input" aria-describedby="cust-kind-help">
                  <option value="cliente">{$t('customers.kinds.cliente')}</option>
                  <option value="fornitore">{$t('customers.kinds.fornitore')}</option>
                  <option value="entrambi">{$t('customers.kinds.entrambi')}</option>
                </select>
              </div>
            </div>
            <p id="cust-kind-help" class="field-hint">{$t('customers.kindHint')}</p>
            </div>

            {#if form.tipo === 'persona_fisica'}
              <div class="sheet-row">
                <div>
                  <label for="cust-nome" class="label">{$t('customers.fields.name')}</label>
                  <input id="cust-nome" bind:value={form.nome} required maxlength="128" autocomplete="off" class="input" />
                </div>
                <div>
                  <label for="cust-cognome" class="label">{$t('customers.fields.surname')}</label>
                  <input id="cust-cognome" bind:value={form.cognome} required maxlength="128" autocomplete="off" class="input" />
                </div>
              </div>
            {:else}
              <div>
                <label for="cust-ragione-sociale" class="label">{$t('customers.fields.companyName')}</label>
                <input id="cust-ragione-sociale" bind:value={form.ragioneSociale} required maxlength="256" autocomplete="off" class="input" />
              </div>
            {/if}

            <div class="sheet-row">
              <div>
                <label for="cust-partita-iva" class="label">{$t('customers.fields.partitaIva')}</label>
                <input id="cust-partita-iva" bind:value={form.partitaIva} maxlength="16" autocomplete="off" class="input font-mono" />
              </div>
              <div>
                <label for="cust-codice-fiscale" class="label">{$t('customers.fields.codiceFiscale')}</label>
                <input id="cust-codice-fiscale" bind:value={form.codiceFiscale} maxlength="16" autocomplete="off" class="input font-mono" />
              </div>
            </div>
            <div>
              <label for="cust-id-paese" class="label">{$t('customers.fields.idPaese')}</label>
              <input id="cust-id-paese" bind:value={form.idPaese} maxlength="2" autocomplete="off"
                pattern={'[A-Za-z]{2}'} aria-describedby="cust-id-paese-help" class="input font-mono uppercase" style="max-width: 7rem;" />
              <p id="cust-id-paese-help" class="field-hint">{$t('customers.fields.idPaeseHint')}</p>
            </div>
          </div>
        </section>

        <section class="sheet-section" aria-labelledby="cust-section-sede">
          <h3 id="cust-section-sede" class="sheet-section-title">{$t('customers.sections.sede.title')}</h3>
          <p class="sheet-section-intro">{$t('customers.sections.sede.intro')}</p>
          {#if sedeVuota}
            <div class="alert alert-warning mb-4" role="note">
              <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
              <p class="alert-message">{$t('customers.sede.emptyNotice')}</p>
            </div>
          {:else if sedeIncompletaInvariata}
            <div class="alert alert-warning mb-4" role="note">
              <AlertTriangle size={18} class="alert-mark mt-0.5" aria-hidden="true" />
              <p class="alert-message">{$t('customers.sede.missingTooltip')}</p>
            </div>
          {/if}
          <div class="sheet-fields">
            <div>
              <div class="sheet-row sheet-row-street">
                <div>
                  <label for="cust-sede-indirizzo" class="label">{$t('customers.sede.fields.indirizzo')}</label>
                  <input id="cust-sede-indirizzo" bind:value={form.sede.indirizzo} maxlength="60" autocomplete="off" class="input"
                    aria-invalid={!!shownSedeErrors.indirizzo}
                    aria-describedby={describedBy('indirizzo', 'cust-sede-street-help', shownSedeErrors)} />
                  {#if shownSedeErrors.indirizzo}
                    <p id="cust-sede-indirizzo-error" class="field-error">{$t(`customers.sede.errors.${shownSedeErrors.indirizzo}`)}</p>
                  {/if}
                </div>
                <div>
                  <label for="cust-sede-numeroCivico" class="label">{$t('customers.sede.fields.numeroCivico')}</label>
                  <input id="cust-sede-numeroCivico" bind:value={form.sede.numeroCivico} maxlength="8" autocomplete="off" class="input"
                    aria-invalid={!!shownSedeErrors.numeroCivico}
                    aria-describedby={describedBy('numeroCivico', 'cust-sede-street-help', shownSedeErrors)} />
                  {#if shownSedeErrors.numeroCivico}
                    <p id="cust-sede-numeroCivico-error" class="field-error">{$t(`customers.sede.errors.${shownSedeErrors.numeroCivico}`)}</p>
                  {/if}
                </div>
              </div>
              <p id="cust-sede-street-help" class="field-hint">{$t('customers.sede.hints.street')}</p>
            </div>

            <div>
              <div class="sheet-row sheet-row-place">
                <div>
                  <label for="cust-sede-cap" class="label">{$t('customers.sede.fields.cap')}</label>
                  <input id="cust-sede-cap" bind:value={form.sede.cap} maxlength="5" inputmode="numeric" autocomplete="off" class="input font-mono"
                    aria-invalid={!!shownSedeErrors.cap}
                    aria-describedby={describedBy('cap', 'cust-sede-place-help', shownSedeErrors)} />
                  {#if shownSedeErrors.cap}
                    <p id="cust-sede-cap-error" class="field-error">{$t(`customers.sede.errors.${shownSedeErrors.cap}`)}</p>
                  {/if}
                </div>
                <div>
                  <label for="cust-sede-comune" class="label">{$t('customers.sede.fields.comune')}</label>
                  <input id="cust-sede-comune" bind:value={form.sede.comune} maxlength="60" autocomplete="off" class="input"
                    aria-invalid={!!shownSedeErrors.comune}
                    aria-describedby={describedBy('comune', 'cust-sede-place-help', shownSedeErrors)} />
                  {#if shownSedeErrors.comune}
                    <p id="cust-sede-comune-error" class="field-error">{$t(`customers.sede.errors.${shownSedeErrors.comune}`)}</p>
                  {/if}
                </div>
                <div>
                  <label for="cust-sede-provincia" class="label">{$t('customers.sede.fields.provincia')}</label>
                  <input id="cust-sede-provincia" bind:value={form.sede.provincia} maxlength="2" autocomplete="off" autocapitalize="characters"
                    class="input font-mono uppercase"
                    aria-invalid={!!shownSedeErrors.provincia}
                    aria-describedby={describedBy('provincia', 'cust-sede-place-help', shownSedeErrors)} />
                  {#if shownSedeErrors.provincia}
                    <p id="cust-sede-provincia-error" class="field-error">{$t(`customers.sede.errors.${shownSedeErrors.provincia}`)}</p>
                  {/if}
                </div>
              </div>
              <p id="cust-sede-place-help" class="field-hint">{$t('customers.sede.hints.place')}</p>
            </div>

            <div>
              <label for="cust-sede-nazione" class="label">{$t('customers.sede.fields.nazione')}</label>
              <input id="cust-sede-nazione" bind:value={form.sede.nazione} on:input={onNazioneInput} maxlength="2"
                autocomplete="off" autocapitalize="characters" class="input font-mono uppercase" style="max-width: 7rem;"
                aria-invalid={!!shownSedeErrors.nazione}
                aria-describedby={describedBy('nazione', 'cust-sede-nazione-help', shownSedeErrors)} />
              {#if shownSedeErrors.nazione}
                <p id="cust-sede-nazione-error" class="field-error">{$t(`customers.sede.errors.${shownSedeErrors.nazione}`)}</p>
              {/if}
              <p id="cust-sede-nazione-help" class="field-hint">
                {sedeEstera ? $t('customers.sede.hints.estero') : $t('customers.sede.hints.nazione')}
              </p>
            </div>
          </div>
        </section>

        <section class="sheet-section" aria-labelledby="cust-section-routing">
          <h3 id="cust-section-routing" class="sheet-section-title">{$t('customers.sections.routing.title')}</h3>
          <p class="sheet-section-intro">{$t('customers.sections.routing.intro')}</p>
          <div class="sheet-fields">
            <div>
              <label for="cust-codice-destinatario" class="label inline-flex items-center gap-1">
                {$t('customers.fields.codiceDestinatario')}<InfoHint text={$t('customers.fields.codiceDestinatarioHint')} />
              </label>
              <!-- 7 caratteri per imprese e privati, 6 per la PA (Codice
                   Univoco Ufficio). Il campo accetta entrambe le lunghezze e
                   avvisa quando non corrisponde al tipo scelto, invece di
                   lasciare che sia SdI a scartare la fattura. -->
              <input id="cust-codice-destinatario" bind:value={form.codiceDestinatario} maxlength="7"
                aria-describedby="cust-codice-destinatario-help"
                pattern={form.tipo === 'pa' ? '[A-Za-z0-9]{6}' : '[A-Za-z0-9]{7}'}
                title={$t('customers.fields.codiceDestinatarioLengthHint', { n: codiceDestinatarioLength })}
                autocomplete="off" class="input font-mono" style="max-width: 12rem;" />
              <p id="cust-codice-destinatario-help" class="field-hint">
                {$t('customers.fields.codiceDestinatarioLengthHint', { n: codiceDestinatarioLength })}
              </p>
              {#if codiceDestinatarioMismatch}
                <p class="mt-1 text-xs text-warning" role="status">
                  {form.tipo === 'pa'
                    ? $t('customers.fields.codiceDestinatarioPaWarning')
                    : $t('customers.fields.codiceDestinatarioB2bWarning')}
                </p>
              {/if}
            </div>
            <div>
              <label for="cust-pec" class="label inline-flex items-center gap-1">
                {$t('customers.fields.pec')}<InfoHint text={$t('customers.fields.pecHint')} />
              </label>
              <input id="cust-pec" bind:value={form.pecDestinatario} type="email" maxlength="256" autocomplete="off" class="input" />
            </div>
          </div>
        </section>

        <section class="sheet-section" aria-labelledby="cust-section-contatti">
          <h3 id="cust-section-contatti" class="sheet-section-title">{$t('customers.sections.contatti.title')}</h3>
          <p class="sheet-section-intro">{$t('customers.sections.contatti.intro')}</p>
          <div class="sheet-fields">
            <div>
              <label for="cust-email" class="label">{$t('customers.fields.email')}</label>
              <input id="cust-email" bind:value={form.email} type="email" maxlength="254" autocomplete="off"
                aria-describedby="cust-email-help" aria-invalid={emailInvalid} class="input" />
              {#if emailInvalid}
                <p class="field-error" role="status">{$t('customers.fields.emailInvalid')}</p>
              {/if}
              <p id="cust-email-help" class="field-hint">{$t('customers.fields.emailHint')}</p>
            </div>
            <div class="sheet-row">
              <div>
                <label for="cust-telefono" class="label">{$t('customers.fields.telefono')}</label>
                <input id="cust-telefono" bind:value={form.telefono} type="tel" maxlength="32" autocomplete="off" class="input" />
              </div>
              <div>
                <label for="cust-fax" class="label">{$t('customers.fields.fax')}</label>
                <input id="cust-fax" bind:value={form.fax} type="tel" maxlength="32" autocomplete="off" class="input" />
              </div>
            </div>
          </div>
        </section>

        <section class="sheet-section" aria-labelledby="cust-section-pagamento">
          <h3 id="cust-section-pagamento" class="sheet-section-title">{$t('customers.sections.pagamento.title')}</h3>
          <p class="sheet-section-intro">{$t('customers.sections.pagamento.intro')}</p>
          <div class="sheet-fields">
            <div class="sheet-row">
              <div>
                <label for="cust-cond-pagamento" class="label">{$t('customers.fields.condPagamento')}</label>
                <select id="cust-cond-pagamento" bind:value={form.condPagamento} class="input">
                  <option value="">{$t('customers.condPagamento.none')}</option>
                  <option value="TP01">{$t('customers.condPagamento.TP01')}</option>
                  <option value="TP02">{$t('customers.condPagamento.TP02')}</option>
                  <option value="TP03">{$t('customers.condPagamento.TP03')}</option>
                </select>
              </div>
              <div>
                <label for="cust-modalita-pagamento" class="label">{$t('customers.fields.payment')}</label>
                <select id="cust-modalita-pagamento" bind:value={form.modalitaPagamento} class="input">
                  <option value="">{$t('customers.paymentMethods.none')}</option>
                  {#each MODALITA_PAGAMENTO as code}
                    <option value={code}>{$t(`customers.paymentMethods.${code}`)}</option>
                  {/each}
                  {#if form.modalitaPagamento && !MODALITA_PAGAMENTO.includes(form.modalitaPagamento)}
                    <option value={form.modalitaPagamento}>{$t('customers.paymentMethods.other', { code: form.modalitaPagamento })}</option>
                  {/if}
                </select>
              </div>
            </div>
            <div class="sheet-row">
              <div>
                <label for="cust-iban" class="label">{$t('customers.fields.iban')}</label>
                <input id="cust-iban" bind:value={form.iban} maxlength="34" autocomplete="off" class="input font-mono" />
              </div>
              <div>
                <label for="cust-scadenza-giorni" class="label">{$t('customers.fields.dueDays')}</label>
                <input id="cust-scadenza-giorni" bind:value={form.scadenzaGiorni} type="number" min="0" max="365" class="input" />
              </div>
            </div>
          </div>
        </section>

        <!-- Recorded once here so a Pubblica Amministrazione under split
             payment does not have to be remembered on every invoice. -->
        <section class="sheet-section" aria-labelledby="cust-section-iva">
          <h3 id="cust-section-iva" class="sheet-section-title">{$t('customers.sections.iva.title')}</h3>
          <p class="sheet-section-intro">{$t('customers.sections.iva.intro')}</p>
          <div>
            <label for="cust-esigibilita" class="label inline-flex items-center gap-1">
              {$t('customers.fields.esigibilitaIva')}<InfoHint text={$t('customers.fields.esigibilitaIvaHint')} />
            </label>
            <select id="cust-esigibilita" bind:value={form.esigibilitaIva} class="input">
              <option value="">{$t('customers.esigibilita.unset')}</option>
              <option value="I">{$t('customers.esigibilita.I')}</option>
              <option value="D">{$t('customers.esigibilita.D')}</option>
              <option value="S">{$t('customers.esigibilita.S')}</option>
            </select>
            <p class="field-hint">{$t('customers.esigibilita.help')}</p>
            <p class="field-hint">{$t('customers.esigibilita.noVatNote')}</p>
          </div>
        </section>

        <section class="sheet-section" aria-labelledby="cust-section-note">
          <h3 id="cust-section-note" class="sheet-section-title">{$t('customers.sections.note.title')}</h3>
          <p class="sheet-section-intro">{$t('customers.sections.note.intro')}</p>
          <label for="cust-note" class="label">{$t('customers.fields.note')}</label>
          <textarea id="cust-note" bind:value={form.note} rows="3" maxlength="2000" class="input"></textarea>
        </section>
      </form>

      <svelte:fragment slot="footer">
        <button type="button" class="btn btn-secondary" on:click={closeSheet} disabled={saving}>{$t('common.cancel')}</button>
        <button type="submit" form="customer-sheet-form" class="btn btn-primary" disabled={saving}>
          {saving ? $t('customers.sheet.saving') : (editingId ? $t('common.save') : $t('common.create'))}
        </button>
        <p class="side-sheet-footer-note">{$t('customers.sheet.footerNote')}</p>
      </svelte:fragment>
    </SideSheet>
  {/if}
</Layout>
